/**
 * routes.ts — the single source of truth for app route paths that are
 * referenced from more than one place (nav, deep links, notification URL
 * router, email CTAs).
 *
 * Why this exists (D22, Phase 1b): the tenant maintenance surface is being
 * cleaved into a dashboard + a dedicated history page, and the admin side
 * gains a read-only Maintenance Record page. Deep-link targets will be
 * retargeted in one place (P1b.11) rather than chased across ~7 hardcoded
 * `/my-maintenance` string literals. Centralizing the paths now (pure
 * refactor — values unchanged) means the later retarget is a one-line edit
 * here instead of a scatter-gather.
 *
 * Server mirror: `functions/src/constants/routes.ts`. Both must stay in
 * sync — the client and server notification URL routers resolve the same
 * paths (golden test in `functions/test`). When you change a path here,
 * change it there too.
 */

export const ROUTES = {
  /** App landing. Post-cutover (P1b.11) this is the tenant's maintenance
   *  surface — equipment list + live scan actions (TenantMaintenanceView). */
  DASHBOARD: '/dashboard',
  /** Legacy tenant maintenance page. Superseded by the dashboard at the P1b.11
   *  cutover; kept as a working fallback for already-sent deep links. */
  MY_MAINTENANCE: '/my-maintenance',
  /** Tenant maintenance event log — the tenant "Maintenance History" nav (P1b.9). */
  MAINTENANCE_HISTORY: '/maintenance-history',
  /** Admin Invitations page (E16 CTA, D83). */
  INVITATIONS: '/admin/invitations',
  /** Admin read-only Maintenance Record page (wired in P1b.5). */
  MAINTENANCE_RECORD: '/maintenance-record',
  /** Admin full review worklist — "View all" from the on-deck strip (D36). */
  MAINTENANCE_REVIEWS: '/maintenance-record/reviews',
  /** Reserved for a future global maintenance surface (D24). */
  MAINTENANCE: '/maintenance',
  /** Contact MNTND support (e.g. to request a tenant move — DU-004). */
  CONTACT: '/contact',
  /** Super-admin/corporate Dev Tools (DevUserCreator). Was `/admin/seed-data`
   *  until 2026-10-13; that path redirects here. */
  DEV_TOOLS: '/admin/dev-tools',
} as const

/**
 * MNTND support email (confirmed 2026-08-18) — the human support inbox.
 * Used by the Contact Us page, error-report mailtos, and the assigned-tenant
 * invite guard (DU-004). Outgoing system mail will move to noreply@mntnd.app
 * (sender-infra task in Notion; extension DEFAULT_FROM, not code).
 */
export const SUPPORT_EMAIL = 'support@mntnd.app'

/**
 * Tenant deep link focused at a specific equipment. The maintenance page
 * reads the `scan` param to scroll/highlight that item (D-N-031). Falls
 * back to the generic dashboard when no equipment is supplied.
 */
export function tenantScanDeepLink(equipmentId?: string): string {
  return equipmentId
    ? `${ROUTES.DASHBOARD}?scan=${encodeURIComponent(equipmentId)}`
    : ROUTES.DASHBOARD
}
